import { useState, useEffect } from 'react';

/**
 * A custom hook that detects if the user has scrolled past a certain threshold.
 * This version uses requestAnimationFrame to avoid performance issues with frequent scroll events.
 * @param scrollThreshold The scroll position (in pixels) to check against. Defaults to 20.
 * @returns `true` if the page is scrolled past the threshold, `false` otherwise.
 */
const useOnScroll = (scrollThreshold = 20) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > scrollThreshold) {
            setIsScrolled(true);
          } else {
            setIsScrolled(false);
          }
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Run on mount to check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollThreshold]);

  return isScrolled;
};

export default useOnScroll;
